import { useState } from "react";
import { Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { AddUpdateTyreBrandDiscountForm } from "modules/TyreDiscountsDashboard/components/AddUpdateTyreBrandDiscountModal/components";
import { useAddTyreBrandDiscountMutation, useUpdateTyreBrandDiscountMutation } from "modules/TyreDiscountsDashboard/components/AddUpdateTyreBrandDiscountModal/hooks";
import "modules/TyreDiscountsDashboard/components/AddUpdateTyreDiscountModal/AddUpdateTyreDiscountModal.scss";
import { toISOString } from "utils/dateHelperFunctions";

import type { DropdownItemProps } from "components";
import type { TYRE_DISCOUNT_CATEGORY, TyreBrandDiscount } from "models";

type AddUpdateTyreBrandDiscountModalProps = {
  brandOptions: DropdownItemProps[];
  categoriesOptions: DropdownItemProps[];
  tyreBrandDiscount: TyreBrandDiscount | null;
  tyreBrandTyreDiscountId: number;
  onClose: () => void;
};

export type AddUpdateTyreBrandDiscountFormValues = Pick<TyreBrandDiscount, "active" | "discount" | "net_margin_euro" | "net_margin_percent"> & {
  brand: number | null;
  category: TYRE_DISCOUNT_CATEGORY | null;
};

const FormHelper = createFormHelper<AddUpdateTyreBrandDiscountFormValues>();

export const AddUpdateTyreBrandDiscountModal = ({
  brandOptions,
  tyreBrandDiscount,
  categoriesOptions,
  tyreBrandTyreDiscountId,
  onClose
}: AddUpdateTyreBrandDiscountModalProps) => {
  const [hasErrors, setHasErrors] = useState(false);

  const { mutateAsync: addTyreBrandDiscount, isPending: isAddTyreBrandDiscountPending } = useAddTyreBrandDiscountMutation();
  const { mutateAsync: updateTyreBrandDiscount, isPending: isUpdateTyreBrandDiscountPending } = useUpdateTyreBrandDiscountMutation();

  const handleClose = () => {
    onClose();
    setHasErrors(false);
  };

  const validateFormData = (values: AddUpdateTyreBrandDiscountFormValues) => {
    const hasErrors =
      values.brand === null ||
      !values.category ||
      (values.discount == null && values.net_margin_euro == null && values.net_margin_percent == null) ||
      (values.discount != null && (values.discount < 0 || values.discount > 99.99)) ||
      (values.net_margin_euro != null && (values.net_margin_euro < 0 || values.net_margin_euro > 99.99)) ||
      (values.net_margin_percent != null && (values.net_margin_percent < 0 || values.net_margin_percent > 99.99));
    setHasErrors(Boolean(hasErrors));
    return hasErrors;
  };

  const handleSubmit = async (values: AddUpdateTyreBrandDiscountFormValues) => {
    if (validateFormData(values)) return;

    const brand = brandOptions.find(({ value }) => value === values.brand)?.text ?? "";

    if (tyreBrandDiscount) {
      await updateTyreBrandDiscount({
        ...tyreBrandDiscount,
        ...values,
        category: values.category || tyreBrandDiscount.category,
        brand: brand || tyreBrandDiscount.brand
      });
    } else {
      if (!values.category || !tyreBrandTyreDiscountId) return;

      await addTyreBrandDiscount({
        ...values,
        created_on: toISOString(new Date()),
        updated_on: toISOString(new Date()),
        id: -1,
        discount: values.discount,
        net_margin_euro: values.net_margin_euro ?? null,
        net_margin_percent: values.net_margin_percent ?? null,
        tyre_discount_id: tyreBrandTyreDiscountId,
        category: values.category,
        brand
      });
    }

    handleClose();
  };

  return (
    <Modal open={Boolean(tyreBrandTyreDiscountId)} onClose={handleClose} closeOnDimmerClick={false}>
      <div className="AddUpdateTyreDiscountModal">
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={
            tyreBrandDiscount
              ? { ...tyreBrandDiscount, brand: brandOptions.find(({ text }) => text === tyreBrandDiscount.brand)?.value ?? null }
              : { active: false, discount: null, net_margin_euro: null, net_margin_percent: null, brand: null, category: null }
          }
        >
          <AddUpdateTyreBrandDiscountForm
            hasErrors={hasErrors}
            isPending={isAddTyreBrandDiscountPending || isUpdateTyreBrandDiscountPending}
            brandOptions={brandOptions}
            tyreBrandDiscount={tyreBrandDiscount}
            categoriesOptions={categoriesOptions}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </FormHelper.Form>
      </div>
    </Modal>
  );
};
